import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { CompanyDTO } from "@shaarp/common";
import { PanelRightCloseIcon, PanelRightOpenIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ItineraryButton } from "./itinerary-button";
import { ItineraryLimitModal } from "./itinerary-limit-modal";

type MapControlsProps = {
  companies: CompanyDTO[];
  isMapOpened: boolean;
  onMapOpenChange: (isOpen: boolean) => void;
  onItineraryStateChange?: (state: MapControlsItineraryState) => void;
};

export type MapControlsItineraryState = {
  shouldCalculateItinerary: boolean;
  companiesForMap: CompanyDTO[];
  sortedCompanies: CompanyDTO[];
  setSortedCompanies: (companies: CompanyDTO[]) => void;
};

const MAX_COMPANIES_FOR_ITINERARY = 10;

export function MapControls({
  companies,
  isMapOpened,
  onMapOpenChange,
  onItineraryStateChange,
}: MapControlsProps) {
  const { t } = useTranslation();

  const [shouldCalculateItinerary, setShouldCalculateItinerary] =
    useState<boolean>(false);
  const [showItineraryLimitModal, setShowItineraryLimitModal] =
    useState<boolean>(false);
  const [sortedCompanies, setSortedCompanies] = useState<CompanyDTO[]>([]);

  const companiesForMap = useMemo(() => {
    if (
      shouldCalculateItinerary &&
      sortedCompanies.length > MAX_COMPANIES_FOR_ITINERARY
    ) {
      return sortedCompanies.slice(0, MAX_COMPANIES_FOR_ITINERARY);
    }
    return sortedCompanies.length > 0 ? sortedCompanies : companies;
  }, [shouldCalculateItinerary, sortedCompanies, companies]);

  const handleItineraryToggle = (
    isMapOpened: boolean,
    onOpenMap: () => void
  ) => {
    if (shouldCalculateItinerary) {
      setShouldCalculateItinerary(false);
      return;
    }

    if (companies.length > MAX_COMPANIES_FOR_ITINERARY) {
      setShowItineraryLimitModal(true);
    } else {
      setShouldCalculateItinerary(true);
      if (!isMapOpened) {
        onOpenMap();
      }
    }
  };

  const handleItineraryConfirm = (
    isMapOpened: boolean,
    onOpenMap: () => void
  ) => {
    setShouldCalculateItinerary(true);
    if (!isMapOpened) {
      onOpenMap();
    }
  };

  const handleMapToggle = (isMapOpened: boolean, onToggleMap: () => void) => {
    if (isMapOpened && shouldCalculateItinerary) {
      setShouldCalculateItinerary(false);
    }
    onToggleMap();
  };

  useEffect(() => {
    if (onItineraryStateChange) {
      onItineraryStateChange({
        shouldCalculateItinerary,
        companiesForMap,
        sortedCompanies,
        setSortedCompanies,
      });
    }
  }, [
    shouldCalculateItinerary,
    companiesForMap,
    sortedCompanies,
    setSortedCompanies,
    onItineraryStateChange,
  ]);

  if (companies.length === 0) return null;

  return (
    <>
      <div className="flex flex-row space-x-4">
        <ItineraryButton
          companiesCount={companies.length}
          isActive={shouldCalculateItinerary}
          onClick={() =>
            handleItineraryToggle(isMapOpened, () => onMapOpenChange(true))
          }
        />
        <Button
          variant="outline"
          onClick={() =>
            handleMapToggle(isMapOpened, () => onMapOpenChange(!isMapOpened))
          }
        >
          {!isMapOpened ? (
            <>
              {t("listing.page.content.map.open")}
              <PanelRightOpenIcon className="ml-2 w-4 h-4" />
            </>
          ) : (
            <>
              {t("listing.page.content.map.close")}
              <PanelRightCloseIcon className="ml-2 w-4 h-4" />
            </>
          )}
        </Button>
      </div>

      <ItineraryLimitModal
        open={showItineraryLimitModal}
        onOpenChange={setShowItineraryLimitModal}
        companiesCount={sortedCompanies.length || companies.length}
        onConfirm={() =>
          handleItineraryConfirm(isMapOpened, () => onMapOpenChange(true))
        }
      />
    </>
  );
}
